import React from 'react';
import './App.css';
import './cards.min.css'
import {Col, Layout, Row, Modal} from 'antd';
import Login from "./components/Login";
import store from "./store";
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { Capacitor } from '@capacitor/core';
import {
    LOGIN_SUCCESSFUL,
    LOGOUT,
    SET_CHAT_MESSAGES, SET_AVAILABLE_HANDS,
    SET_CARDS,
    SET_CARDS_IN_THE_MIDDLE,
    SET_CURRENT_PLAYER, SET_CURRENT_HAND, SET_PLAYERS, SET_PREVIOUS_TAKER, SET_PUNISHMENTS, SET_SCORES,
    SET_AUTH_STATUS, SET_DEEPLINK_DATA, SET_GAME_MODE
} from "./actions";
import ScoreBoard from "./components/ScoreBoard";
import GameBoard from "./components/GameBoard";
// Removed unused import: getWebSocket
import Notes from "./components/Notes";
import Support from "./components/Support";
import PrivacyPolicy from "./components/PrivacyPolicy";
import BlockStoreRedirect from "./components/BlockStoreRedirect";

const { Content } = Layout;

// Define the store state interface
interface StoreState {
  kingGame: {
    gameState: {
      chatMessages: { message: string; sender: string }[];
      isLoggedIn: boolean;
      username: string;
      cards: string[];
      cardsInTheMiddle: string[];
      playerId: number;
      availableHands: string[];
      currentHand: string;
      currentPlayer: number;
      currentPlayerName: string;
      scores: string[];
      players: string[];
      previousCards: string[];
      previousTaker: string;
      room: string;
      punishments: string[];
      authProvider: string | null;
      authToken: string | null;
      userProfile: any | null;
      isAuthenticated: boolean;
      authType: string | null;
      gameMode: string | null;
      singlePlayer: boolean;
      deeplinkRoomId: string | null;
      deeplinkInviteCode: string | null;
      isDeeplinkFlow: boolean;
    }
  }
}

// Define the component state interface
interface AppState {
  isLoggedIn: boolean;
  username: string;
  room: string;
  showScoreBoardModal: boolean;
  showChatModal: boolean;
  pendingCardClear?: {
    trickWinner: string;
    trickCards: string[];
    clearData: any;
  };
}

class App extends React.Component<any, AppState> {
    private animationTimeouts: NodeJS.Timeout[] = [];

    constructor(props: any) {
        super(props);
        this.state = {
            isLoggedIn: false,
            username: "",
            room: "",
            showScoreBoardModal: false,
            showChatModal: false,
            pendingCardClear: undefined
        }
    }

    componentDidMount(): void {
        // Store reference to this component for external access
        (window as any).app = this;
        
        // Force portrait orientation on app start (login screen)
        if (Capacitor.isNativePlatform()) {
            ScreenOrientation.lock({ orientation: 'portrait' }).catch(err => 
                console.log('Initial portrait lock failed:', err)
            );
        }
        
        // Check for deeplinks first
        this.checkForDeeplink();
        
        // Check for existing session on startup
        this.checkExistingSession();
        
        store.subscribe(() => {
            let state = store.getState() as StoreState;
            const wasLoggedIn = this.state.isLoggedIn;
            const isNowLoggedIn = state.kingGame.gameState.isLoggedIn;
            
            this.setState({
                isLoggedIn: isNowLoggedIn,
                username: state.kingGame.gameState.username,
                room: state.kingGame.gameState.room
            });
            
            // Make room available globally for GameBoard
            (window as any).currentRoom = state.kingGame.gameState.room;
            
            // Save session data when logged in (with a small delay to ensure all state is updated)
            if (isNowLoggedIn && !wasLoggedIn) {
                setTimeout(() => {
                    const currentState = store.getState() as StoreState;
                    this.saveSession(currentState.kingGame.gameState.username, currentState.kingGame.gameState.room);
                }, 100);
            }
            
            // Clear session data when logged out
            if (wasLoggedIn && !isNowLoggedIn) {
                this.clearSession();
                // Clear deeplink URL when user leaves room
                this.clearDeeplinkUrl();
            }

            // Handle orientation change on login/logout
            if (Capacitor.isNativePlatform()) {
                if (!wasLoggedIn && isNowLoggedIn) {
                    // Just logged in - switch to landscape for game
                    ScreenOrientation.lock({ orientation: 'landscape' }).catch(err => 
                        console.log('Orientation lock failed:', err)
                    );
                } else if (wasLoggedIn && !isNowLoggedIn) {
                    // Just logged out - force portrait for login screen
                    ScreenOrientation.lock({ orientation: 'portrait' }).catch(err => 
                        console.log('Orientation lock to portrait failed:', err)
                    );
                }
            }
        });

    }

    // Check for deeplink parameters in URL
    checkForDeeplink = () => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const roomId = urlParams.get('room') || urlParams.get('roomId');
            const inviteCode = urlParams.get('invite') || urlParams.get('inviteCode');
            
            console.log('Checking for deeplink:', { roomId, inviteCode });
            
            if (roomId || inviteCode) {
                console.log('Deeplink detected:', { roomId, inviteCode });
                store.dispatch({
                    type: SET_DEEPLINK_DATA,
                    roomId,
                    inviteCode
                });
                
                // Don't clear URL immediately - wait until successful login
                // URL will be cleared in clearDeeplinkUrl() after successful join
            }
        } catch (error) {
            console.error('Error checking for deeplink:', error);
        }
    };

    // Update URL with room ID when joining a room (multiplayer only)
    updateUrlWithRoom = (roomId: string) => {
        try {
            if (!Capacitor.isNativePlatform()) { // Only update URL on desktop/web
                // Check if this is a multiplayer game before updating URL
                const currentState = store.getState() as StoreState;
                const gameMode = currentState.kingGame.gameState.gameMode;
                const singlePlayer = currentState.kingGame.gameState.singlePlayer;
                
                // Only update URL for multiplayer games
                if (gameMode === 'multiplayer' || (!singlePlayer && gameMode !== 'single')) {
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.set('room', roomId);
                    window.history.replaceState({}, document.title, newUrl.toString());
                    console.log('URL updated with room ID for multiplayer game:', roomId);
                } else {
                    console.log('Single player game - not updating URL with room ID');
                }
            }
        } catch (error) {
            console.error('Error updating URL with room:', error);
        }
    };

    // Clear deeplink URL when user leaves room (logout)
    clearDeeplinkUrl = () => {
        try {
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('room');
            newUrl.searchParams.delete('roomId');
            newUrl.searchParams.delete('invite');
            newUrl.searchParams.delete('inviteCode');
            window.history.replaceState({}, document.title, newUrl.toString());
            console.log('Deeplink URL cleared after logout');
        } catch (error) {
            console.error('Error clearing deeplink URL:', error);
        }
    };

    // Check if user is authenticated (has valid OAuth session)
    checkAuthenticationStatus = async (): Promise<boolean> => {
        try {
            const isDevelopment = process.env.NODE_ENV === 'development';
            const backendUrl = isDevelopment ? 'http://localhost:8080' : 'https://gamegarden.co';
            
            const response = await fetch(`${backendUrl}/api/auth/user`, {
                credentials: 'include',
                mode: 'cors'
            });
            
            if (response.ok) {
                const userData = await response.json();
                console.log('User is authenticated:', userData);
                
                store.dispatch({
                    type: SET_AUTH_STATUS,
                    isAuthenticated: true,
                    authType: 'google'
                });
                
                return true;
            } else {
                console.log('User is not authenticated');
                store.dispatch({
                    type: SET_AUTH_STATUS,
                    isAuthenticated: false,
                    authType: null
                });
                
                return false;
            }
        } catch (error) {
            console.log('Authentication check failed:', error);
            store.dispatch({
                type: SET_AUTH_STATUS,
                isAuthenticated: false,
                authType: null
            });
            
            return false;
        }
    };

    // Set up WebSocket event handlers when connection is established
    setupWebSocketHandlers = (ws: WebSocket) => {
        ws.onopen = () => {
            console.log("Connected to server");
            console.log("WebSocket connection established successfully");
        }

        ws.onmessage = evt => {
            // on receiving a message, add it to the list of messages
            console.log("Raw message received:", evt.data);
            
            try {
                const message = JSON.parse(evt.data)
                console.log("Parsed message:", message)
                console.log("Message ID:", message.id)
                console.log("Message data:", message.data)

            switch (message.id) {
                case "existingParticipants":
                    // This message indicates successful room join - treat as login success
                    console.log("Received existingParticipants - login successful");
                    const pendingLogin = (window as any).pendingLogin;
                    console.log("Pending login data:", pendingLogin);
                    
                    if (pendingLogin) {
                        console.log("Dispatching LOGIN_SUCCESSFUL with:", {
                            username: pendingLogin.username,
                            room: pendingLogin.room,
                            authProvider: pendingLogin.authProvider
                        });
                        
                        // Get current state to preserve authType and gameMode
                        const currentState = store.getState() as StoreState;
                        
                        store.dispatch({
                            type: LOGIN_SUCCESSFUL,
                            text: pendingLogin.username,
                            room: pendingLogin.room,
                            authProvider: pendingLogin.authProvider,
                            authToken: pendingLogin.authToken,
                            userProfile: pendingLogin.userProfile,
                            authType: pendingLogin.isGuest ? 'guest' : 'google',
                            gameMode: currentState.kingGame.gameState.gameMode,
                            isAuthenticated: !pendingLogin.isGuest
                        });
                        
                        // Update URL with room ID for sharing
                        if (pendingLogin.room) {
                            this.updateUrlWithRoom(pendingLogin.room);
                        }
                        
                        // Clear the pending login info
                        delete (window as any).pendingLogin;
                    } else {
                        console.warn("No pending login data found when existingParticipants received");
                    }
                    
                    // Clear any loading state and timeout in the Login component
                    if ((window as any).loginComponent && (window as any).loginComponent.clearLoginTimeout) {
                        (window as any).loginComponent.clearLoginTimeout();
                    }
                    // Clear reconnection timeout if it exists (successful reconnection)
                    if ((window as any).reconnectionTimeout) {
                        clearTimeout((window as any).reconnectionTimeout);
                        delete (window as any).reconnectionTimeout;
                        console.log('Reconnection successful - timeout cleared');
                    }
                    break;
                case "chat":
                    store.dispatch({
                        type: SET_CHAT_MESSAGES,
                        messages: message.data
                    });
                    break;
                case "cards":
                    store.dispatch({
                        type: SET_CARDS,
                        text: message.data,
                        playerId: message.playerId
                    });
                    break;
                case "cardsInTheMiddle":
                    // If we're clearing cards (empty array) and there were cards before, coordinate with taker info
                    const currentState = store.getState() as StoreState;
                    const currentCards = currentState.kingGame.gameState.cardsInTheMiddle;
                    const newCards = message.data;
                    
                    console.log("cardsInTheMiddle message:", { 
                        newCardsLength: newCards.length, 
                        currentCardsLength: currentCards.length,
                        newCards,
                        currentCards
                    });
                    
                    if (newCards.length === 0 && currentCards.length >= 3) {
                        console.log("Trick completed! Checking for taker info...");
                        // All 4 cards were played - check if we have taker info
                        if (this.state.pendingCardClear && this.state.pendingCardClear.trickWinner && this.state.pendingCardClear.trickCards) {
                            console.log("Have taker info, animating immediately:", this.state.pendingCardClear.trickWinner);
                            // We already have taker info, animate immediately
                            this.animateCardsToWinner(this.state.pendingCardClear.trickWinner, this.state.pendingCardClear.trickCards, newCards);
                            this.setState({ pendingCardClear: undefined });
                        } else {
                            console.log("Waiting for taker info...");
                            // Wait for taker info
                            this.setState({ 
                                pendingCardClear: { 
                                    trickWinner: '', 
                                    trickCards: [],
                                    clearData: newCards 
                                } 
                            });
                        }
                    } else {
                        // Normal case - update immediately
                        // But first clear any pending animations to prevent timing conflicts
                        if (newCards.length > 0) {
                            console.log("New cards being played, clearing any pending animations");
                            this.clearPendingAnimations();
                        }
                        
                        store.dispatch({
                            type: SET_CARDS_IN_THE_MIDDLE,
                            text: newCards
                        });
                    }
                    break;
                case "previousTaker":
                    console.log("previousTaker message:", { player: message.player, data: message.data });
                    // Update the previous taker info
                    store.dispatch({
                        type: SET_PREVIOUS_TAKER,
                        text: message.data,
                        player: message.player
                    });
                    
                    // Check if we have pending card clear waiting for taker info
                    if (this.state.pendingCardClear && this.state.pendingCardClear.trickWinner === '') {
                        console.log("Got taker info, starting animation:", message.player);
                        // Start animation with the taker info and trick cards
                        this.animateCardsToWinner(message.player, message.data, this.state.pendingCardClear.clearData);
                        this.setState({ pendingCardClear: undefined });
                    } else if (this.state.pendingCardClear) {
                        console.log("Updating pending clear with taker info:", message.player);
                        // Update taker info in pending clear
                        this.setState({
                            pendingCardClear: {
                                ...this.state.pendingCardClear,
                                trickWinner: message.player,
                                trickCards: message.data
                            }
                        });
                    }
                    break;
                case "currentPlayer":
                    store.dispatch({
                        type: SET_CURRENT_PLAYER,
                        text: message.playerId,
                        name: message.name
                    });
                    break;
                case "availableHands":
                    store.dispatch({
                        type: SET_AVAILABLE_HANDS,
                        text: message.data
                    });
                    break;
                case "currentHand":
                    store.dispatch({
                        type: SET_CURRENT_HAND,
                        text: message.name
                    });
                    break;
                case "scores":
                    store.dispatch({
                        type: SET_SCORES,
                        text: message.data
                    });
                    break;
                case "players":
                    store.dispatch({
                        type: SET_PLAYERS,
                        text: message.data
                    });
                    break;
                case "punishments":
                    store.dispatch({
                        type: SET_PUNISHMENTS,
                        text: message.data
                    });
                    break;
                case "loginSuccessful":
                case "login":
                case "loginSuccess":
                    console.log("Login successful, dispatching to store:", message);
                    store.dispatch({
                        type: LOGIN_SUCCESSFUL,
                        text: message.username || message.name || message.text,
                        room: message.room
                    });
                    // Clear any loading state and timeout in the Login component
                    if ((window as any).loginComponent && (window as any).loginComponent.clearLoginTimeout) {
                        (window as any).loginComponent.clearLoginTimeout();
                    }
                    break;
                default:
                    console.log("Unknown message type:", message.id);
                    console.log("Full message:", message);
                    
                    // If we receive any message and we're in loading state, it might be a login response
                    // Check if the message contains login-related data
                    if (message.username || message.name || message.room) {
                        console.log("Possible login response detected:", message);
                        store.dispatch({
                            type: LOGIN_SUCCESSFUL,
                            text: message.username || message.name,
                            room: message.room
                        });
                    }
            }
            } catch (error) {
                console.error("Error parsing message:", error);
                console.log("Raw message that failed to parse:", evt.data);
            }
        }

        ws.onclose = () => {
            console.log("Disconnected from server");
            console.log("WebSocket connection closed");
        }

        ws.onerror = (error) => {
            console.error("WebSocket error:", error);
            console.log("WebSocket error details:", error);
        }
    }

    // Session management methods
    saveSession = (username: string, room: string) => {
        try {
            const state = store.getState() as StoreState;
            const sessionData = {
                username,
                room,
                timestamp: Date.now(),
                authType: state.kingGame.gameState.authType,
                isAuthenticated: state.kingGame.gameState.isAuthenticated,
                gameMode: state.kingGame.gameState.gameMode,
                authProvider: state.kingGame.gameState.authProvider,
                authToken: state.kingGame.gameState.authToken,
                userProfile: state.kingGame.gameState.userProfile
            };
            localStorage.setItem('kingGameSession', JSON.stringify(sessionData));
            console.log('✅ Session saved successfully:', {
                username,
                room,
                authType: sessionData.authType,
                gameMode: sessionData.gameMode,
                isAuthenticated: sessionData.isAuthenticated
            });
        } catch (error) {
            console.error('❌ Error saving session:', error);
        }
    };

    clearSession = () => {
        try {
            localStorage.removeItem('kingGameSession');
            console.log('Session cleared');
        } catch (error) {
            console.error('Error clearing session:', error);
        }
    };

    checkExistingSession = async () => {
        try {
            const sessionData = localStorage.getItem('kingGameSession');
            
            if (sessionData) {
                const parsedSession = JSON.parse(sessionData);
                const { 
                    username, 
                    room, 
                    timestamp, 
                    authType,
                    authProvider, 
                    authToken, 
                    userProfile,
                    gameMode,
                    isAuthenticated 
                } = parsedSession;
                
                // Check if session is not too old (24 hours)
                const sessionAge = Date.now() - timestamp;
                const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
                
                if (sessionAge < maxAge && username && room) {
                    console.log('Found valid session, attempting to reconnect:', { 
                        username, 
                        room, 
                        authType,
                        gameMode 
                    });
                    
                    // For Google auth sessions, verify the authentication is still valid
                    if (authType === 'google' && isAuthenticated) {
                        const isStillAuthenticated = await this.checkAuthenticationStatus();
                        if (!isStillAuthenticated) {
                            console.log('Google authentication expired, clearing session');
                            this.clearSession();
                            return;
                        }
                    }
                    
                    // Set auth status in Redux before reconnection
                    store.dispatch({
                        type: SET_AUTH_STATUS,
                        isAuthenticated: authType === 'google' ? isAuthenticated : true,
                        authType
                    });
                    
                    // Set game mode if available
                    if (gameMode) {
                        store.dispatch({ type: SET_GAME_MODE, gameMode });
                    }
                    
                    // Store pending login info before attempting reconnection
                    (window as any).pendingLogin = {
                        username,
                        room,
                        authProvider,
                        authToken,
                        userProfile,
                        isGuest: authType === 'guest'
                    };
                    
                    this.attemptReconnection(username, room, { 
                        authProvider, 
                        authToken, 
                        userProfile, 
                        isGuest: authType === 'guest',
                        authType,
                        gameMode
                    });
                    return;
                } else {
                    console.log('Session expired or invalid, clearing...');
                    this.clearSession();
                }
            }
            
            // Check for Google authentication status even without local session
            const isGoogleAuthenticated = await this.checkAuthenticationStatus();
            if (isGoogleAuthenticated) {
                console.log('Found valid Google authentication but no local session');
                // Don't auto-reconnect, but let Login component handle this
                return;
            }
            
        } catch (error) {
            console.error('Error checking existing session:', error);
            this.clearSession();
        }
    };

    attemptReconnection = (username: string, room: string, sessionData?: { 
        authProvider?: string | null, 
        authToken?: string | null, 
        userProfile?: any | null, 
        isGuest?: boolean,
        authType?: string | null,
        gameMode?: string | null
    }) => {
        console.log('Starting reconnection attempt for:', { username, room, authType: sessionData?.authType });
        
        // First initialize WebSocket connection, then attempt reconnection
        const { initializeWebSocket } = require('./Socket');
        
        initializeWebSocket().then((ws: WebSocket) => {
            console.log('WebSocket ready, setting up handlers...');
            this.setupWebSocketHandlers(ws);
            
            console.log('WebSocket ready, sending reconnection request...');
            
            // Determine if this is a single player game based on room ID format
            // Single player games typically have UUID format room names
            const isSinglePlayer = sessionData?.gameMode === 'single' || 
                                   /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(room);
            
            console.log('Detected game mode:', { 
                gameMode: sessionData?.gameMode, 
                isSinglePlayer, 
                roomFormat: room.length 
            });
            
            // Send join room message with session data
            const data = {
                id: "joinRoom",
                name: username,
                room: room,
                singlePlayer: isSinglePlayer,
                oauthProvider: sessionData?.authProvider,
                oauthToken: sessionData?.authToken,
                isAuthenticated: sessionData?.authType === 'google',
                isGuest: sessionData?.authType === 'guest'
            };
            
            try {
                ws.send(JSON.stringify(data));
                console.log('Reconnection request sent:', data);
                
                // Set up timeout for reconnection attempt
                const reconnectionTimeout = setTimeout(() => {
                    console.warn('Reconnection timeout - clearing session and showing login');
                    this.clearSession();
                    store.dispatch({ type: LOGOUT });
                }, 10000); // 10 second timeout
                
                // Clear timeout if reconnection succeeds (handled in WebSocket message handlers)
                (window as any).reconnectionTimeout = reconnectionTimeout;
                
            } catch (error) {
                console.error('Error sending reconnection request:', error);
                this.clearSession();
                store.dispatch({ type: LOGOUT });
            }
        }).catch((error: any) => {
            console.error('Failed to establish WebSocket connection for reconnection:', error);
            this.clearSession();
            store.dispatch({ type: LOGOUT });
        });
    };

    // Clear any pending animation timeouts
    clearPendingAnimations(): void {
        console.log("Clearing pending animations, count:", this.animationTimeouts.length);
        this.animationTimeouts.forEach(timeout => clearTimeout(timeout));
        this.animationTimeouts = [];
    }

    // Helper method to get player position relative to current player
    getPlayerDirection(trickWinner: string): string {
        const state = store.getState() as StoreState;
        const players = state.kingGame.gameState.players;
        const playerId = state.kingGame.gameState.playerId;
        
        if (!players || players.length === 0) return 'bottom';
        
        // Find the winner's ID from players array
        let winnerId = -1;
        for (const player of players) {
            const [id, name] = player.split('#');
            if (name === trickWinner) {
                winnerId = parseInt(id);
                break;
            }
        }
        
        if (winnerId === -1 || winnerId === playerId) return 'bottom';
        
        // Calculate relative position (0=bottom/self, 1=right, 2=top, 3=left)
        const relativePosition = (winnerId - playerId + 4) % 4;
        
        switch (relativePosition) {
            case 1: return 'right';
            case 2: return 'top';
            case 3: return 'left';
            default: return 'bottom';
        }
    }

    // Method to trigger card collection animation
    animateCardsToWinner(trickWinner: string, trickCards: string[], clearData: any): void {
        console.log("animateCardsToWinner called:", { trickWinner, trickCards, clearData });
        
        // Clear any previous pending animations first
        this.clearPendingAnimations();
        
        const direction = this.getPlayerDirection(trickWinner);
        console.log("Animation direction:", direction);
        
        // First, display the trick cards in the center temporarily
        store.dispatch({
            type: SET_CARDS_IN_THE_MIDDLE,
            text: trickCards
        });
        
        // Wait a moment for the DOM to update, then animate
        const domUpdateTimeout = setTimeout(() => {
            const centerCards = document.querySelectorAll('.center-play-area .card');
            console.log("Found center cards for animation:", centerCards.length, centerCards);
            
            if (centerCards.length === 0) {
                console.warn("No cards found in center-play-area!");
                // Fallback - clear immediately if no cards found
                const fallbackTimeout = setTimeout(() => {
                    console.log("Fallback: clearing cards after no cards found");
                    store.dispatch({
                        type: SET_CARDS_IN_THE_MIDDLE,
                        text: clearData
                    });
                }, 100);
                this.animationTimeouts.push(fallbackTimeout);
                return;
            }
            
            centerCards.forEach((card, index) => {
                const staggerTimeout = setTimeout(() => {
                    const animationClass = `card-collect-to-${direction}`;
                    console.log(`Adding animation class "${animationClass}" to card ${index}:`, card);
                    card.classList.add(animationClass);
                }, index * 150); // Stagger animation by 150ms per card
                this.animationTimeouts.push(staggerTimeout);
            });
            
            // Clear cards after animation completes
            const clearTimeout = setTimeout(() => {
                console.log("Animation complete, clearing cards for trick winner:", trickWinner);
                store.dispatch({
                    type: SET_CARDS_IN_THE_MIDDLE,
                    text: clearData
                });
                // Remove this timeout from tracking since it's completed
                this.animationTimeouts = this.animationTimeouts.filter(t => t !== clearTimeout);
            }, 1500); // Animation duration
            this.animationTimeouts.push(clearTimeout);
        }, 100); // Small delay to let DOM update
        this.animationTimeouts.push(domUpdateTimeout);
    }

    // Logout handler
    handleLogout = async () => {
        console.log('Logout initiated');
        
        try {
            // For Google login users, call backend logout endpoint to clear server session
            const state = store.getState() as StoreState;
            const isGoogleAuth = state.kingGame.gameState.authType === 'google';
            
            if (isGoogleAuth) {
                const isDevelopment = process.env.NODE_ENV === 'development';
                const backendUrl = isDevelopment ? 'http://localhost:8080' : 'https://gamegarden.co';
                
                try {
                    console.log('Calling backend logout endpoint for Google user');
                    await fetch(`${backendUrl}/logout`, {
                        method: 'GET',
                        credentials: 'include',
                        mode: 'cors'
                    });
                    console.log('Backend logout successful');
                } catch (error) {
                    console.warn('Backend logout failed, continuing with local logout:', error);
                }
            }
        } catch (error) {
            console.error('Error during logout:', error);
        }
        
        // Clear all local state and storage
        this.clearSession();
        
        // Clear any pending login data
        delete (window as any).pendingLogin;
        
        // Clear authentication cookies by setting them to expire
        document.cookie.split(";").forEach(function(c) { 
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        
        // Reset authentication status in Redux
        store.dispatch({
            type: SET_AUTH_STATUS,
            isAuthenticated: false,
            authType: null
        });
        
        // Dispatch logout action to completely reset Redux state
        store.dispatch({ type: LOGOUT });
        
        console.log('Logout completed - all state cleared');
    };

    // Modal handlers
    showScoreBoardModal = () => this.setState({ showScoreBoardModal: true });
    hideScoreBoardModal = () => this.setState({ showScoreBoardModal: false });
    
    showChatModal = () => this.setState({ showChatModal: true });
    hideChatModal = () => this.setState({ showChatModal: false });

    render(): React.ReactNode {
        const isLoggedIn = this.state.isLoggedIn;

        // Check if this is the support page route
        if (window.location.pathname === '/support') {
            return <Support onBack={() => window.location.href = '/'} />
        }

        // Check if this is the privacy policy page route
        if (window.location.pathname === '/privacy') {
            return <PrivacyPolicy />
        }

        // Smart store link for Block Drop Puzzle Blast
        if (window.location.pathname === '/block') {
            return <BlockStoreRedirect />
        }

        if (!isLoggedIn) {
            return <Login />
        }

        return (
            <Layout style={{ 
                minHeight: '100vh',
                background: 'transparent'
            }}>
                <Content className="ant-layout-content" style={{ 
                    background: 'transparent',
                    height: '100vh',
                    minHeight: '100vh',
                    maxHeight: '100vh',
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    <Row gutter={[0, 0]} style={{ height: '100%', width: '100%', maxWidth: '100%', margin: 0 }}>
                        {/* Main Game Area - Full width on mobile */}
                        <Col xs={24} sm={24} md={24} lg={24} xl={18} className="main-game-area" style={{ height: '100%', width: '100%', maxWidth: '100%' }}>
                            <GameBoard 
                                onShowScoreBoard={this.showScoreBoardModal}
                                onShowChat={this.showChatModal}
                                onLogout={this.handleLogout}
                            />
                            
                        </Col>

                        {/* Sidebar - Hidden on mobile */}
                        <Col xs={0} sm={0} md={0} lg={0} xl={6} className="desktop-only" style={{ height: '100%' }}>
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                height: '100%',
                                gap: 12,
                                overflow: 'hidden'
                            }}>
                                <div style={{ flex: '0 0 auto', maxHeight: '40%' }}>
                                    <ScoreBoard />
                                </div>
                                <div style={{ flex: '1 1 auto', minHeight: '250px', overflow: 'hidden' }}>
                                    <Notes />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Content>


                {/* Mobile Modals */}
                <Modal
                    open={this.state.showScoreBoardModal}
                    onCancel={this.hideScoreBoardModal}
                    footer={null}
                    width="90%"
                    style={{ maxWidth: 400 }}
                >
                    <ScoreBoard />
                </Modal>

                <Modal
                    open={this.state.showChatModal}
                    onCancel={this.hideChatModal}
                    footer={null}
                    width="90%"
                    style={{ maxWidth: 400 }}
                >
                    <Notes />
                </Modal>

            </Layout>
        );
    }
}

export default App;

